import React from "react";
import { Form } from "react-bootstrap";
import { ComponentProps } from "@util/types";
import styled from "styled-components";
import { colors } from "@theme/theming";

type Props = ComponentProps<typeof Form.Check> & {
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	value: boolean;
	id?: string;
	label: string;
	className?: string;
	css?: string;
	disabled?: boolean;
};

const CheckboxButton = React.forwardRef<HTMLInputElement, Props>(
	(props: Props, ref) => {
		const { onChange, value, id, label, className, css, disabled, ...rest } =
			props;
		const inputId = id || `checkbox-${Math.random().toString(36).substring(2)}`;

		return (
			<ButtonContainer
				as="label"
				htmlFor={inputId}
				className={className}
				css={css}
				$checked={value}
				$disabled={disabled}
			>
				<HiddenCheckbox
					type="checkbox"
					{...rest}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						onChange(e);
					}}
					checked={value}
					disabled={disabled}
					id={inputId}
					ref={ref}
				/>
				<ButtonLabel $checked={value} $disabled={disabled}>
					{label}
				</ButtonLabel>
			</ButtonContainer>
		);
	}
);

const ButtonContainer = styled.div<{
	$checked: boolean;
	$disabled?: boolean;
	css?: string;
}>`
	display: inline-box;
	align-items: center;
	padding: 6px 12px;
	margin: 8px 8px 0 8px;
	background: transparent;
	border: 1px solid
		${(props) => (props.$checked ? colors.primary.p200 : "#dee2e6")};
	border-radius: 4px;
	cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
	transition: border-color 0.2s ease;
	opacity: ${(props) => (props.$disabled ? 0.5 : 1)};

	&:hover {
		border-color: ${(props) =>
			props.$disabled
				? props.$checked
					? colors.primary.p200
					: "#dee2e6"
				: props.$checked
				? colors.primary.p300
				: "#adb5bd"};
	}

	${(props) => props.css}
`;

const HiddenCheckbox = styled.input`
	display: none;
`;

const ButtonLabel = styled.span<{ $checked: boolean; $disabled?: boolean }>`
	margin: 0;
	cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
	font-size: 14px;
	color: ${(props) => (props.$checked ? colors.primary.p200 : "#495057")};
	font-weight: ${(props) => (props.$checked ? "500" : "400")};
`;

export default CheckboxButton;
